import { Card, CardContent, CardFooter } from '@ui/card.tsx';
import {
  CopyIcon,
  FileKeyIcon,
  InfoIcon,
  SquareArrowOutUpRightIcon,
  TriangleAlertIcon
} from 'lucide-react';
import { Button } from '@ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@ui/dialog.tsx';

import { useEffect, useState } from 'react';
import { Input } from '@ui/input.tsx';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useApiAdminapitokensGetCollection } from '@r/api/generated/client.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useTokenColumns } from '@admin/components/api/token/token-columns.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { ApiTokenForm } from '@admin/components/forms/api-token/api-token-form.tsx';
import type { ApiTokenApiTokenReadModel } from '@r/api/generated/client.schemas.ts';
import { ApiTokenToDeleteForm } from '@admin/components/forms/api-token/api-token-remove-form.tsx';
import { ApiTokenDataTableSkeleton } from '@admin/components/datatable/api-token-data-table-skeleton.tsx';
import { Link } from '@tanstack/react-router';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Token = ApiTokenApiTokenReadModel;

export default function ApiToken() {
  const { t } = useTranslation();

  const [tokenName, setTokenName] = useState('');
  const [tokenGenerated, setTokenGenerated] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState<Token | null>(null);
  const [newTokenDialogOpen, setNewTokenDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [clipboardInputValue, setClipboardInputValue] = useState('');

  useEffect(() => {
    if (!confirmDialogOpen && tokenToDelete) {
      setTokenToDelete(null);
    }
  }, [confirmDialogOpen]);

  const {
    data: apiTokens,
    isLoading: isApiTokensLoading,
    isSuccess: isApiTokensSuccess,
    isError: isApiTokensError,
    error: apiTokensError,
    errorUpdatedAt: apiTokensErrorUpdatedAt,
    refetch: refetchApiTokens
  } = useApiAdminapitokensGetCollection();
  useOrvalQueryErrorToast(
    { isError: isApiTokensError, error: apiTokensError, errorUpdatedAt: apiTokensErrorUpdatedAt },
    t('toast.error.label')
  );

  const confirmTokenDeletion = (token: Token) => {
    if (token) {
      setTokenToDelete(token);
      setConfirmDialogOpen(true);
    }
  };

  const { table } = useDataTable({
    data: apiTokens ?? [],
    columns: useTokenColumns(confirmTokenDeletion),
    pageCount: 1
  });

  const handleApiTokenGenerationSuccess = (token: string, tokenName: string) => {
    refetchApiTokens().then(() => {
      setClipboardInputValue(token);
      setTokenName(tokenName);
      setTokenGenerated(true);
      setNewTokenDialogOpen(false);
    });
  };

  const handleApiTokenDeletionSuccess = () => {
    refetchApiTokens().then(() => {
      if (apiTokens) {
        const lastTokenListItem = apiTokens.at(-1);

        if (tokenToDelete?.id === lastTokenListItem?.id) {
          setTokenGenerated(false);
        }
      }

      setConfirmDialogOpen(false);
    });
  };

  const handleCopy = () => {
    if (clipboardInputValue) {
      navigator.clipboard
        .writeText(clipboardInputValue)
        .then(() => {
          toast.success(t('common.clipboard.success'));
        })
        .catch(() => {
          toast.error(t('common.clipboard.error'));
        });
    }
  };

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('admin.api.title')}</h1>

      {isApiTokensLoading && (
        <Card>
          <CardContent className="pt-6">
            <ApiTokenDataTableSkeleton columnCount={4} rowCount={4} />
          </CardContent>
        </Card>
      )}

      {isApiTokensSuccess && (
        <Card>
          {apiTokens.length > 0 && (
            <CardContent className="pt-6 space-y-2.5">
              <div className="w-2/3 space-y-2.5 pb-7">
                <h2 className="text-3xl font-bold">{t('admin.api.token.title')}</h2>

                <p className="text-muted-foreground">{t('admin.api.token.description')}</p>

                <Link
                  to={'/settings/api/tokens/doc'}
                  target={'_blank'}
                  className={
                    'flex items-center bg-muted hover:bg-muted-foreground hover:text-background rounded-sm px-2.5 py-0.5 w-fit hover:duration-300 ease-in-out'
                  }
                >
                  {t('admin.api.token.docs.button')}
                  <SquareArrowOutUpRightIcon size={15} className="ml-1" />
                </Link>
              </div>

              <div className="mb-2.5 flex justify-between">
                <div className="flex items-center space-x-2.5">
                  <InfoIcon className="stroke-info" size={20} />
                  <p className="text-base">{t('admin.api.token.information', { tokenMax: 10 })}</p>
                </div>

                <div>
                  {t('admin.api.token.count', { tokenCount: apiTokens.length, tokenMax: 10 })}
                </div>
              </div>

              <DataTable table={table} pagination={false} />

              {tokenGenerated && (
                <Card className="mt-7 border-0 bg-muted">
                  <CardContent className="pt-6">
                    <div className="flex flex-col w-2/3">
                      <div className="flex items-center space-x-2.5 mb-5">
                        <TriangleAlertIcon className="stroke-warning" size={25} />
                        <p className="text-base">{t('admin.api.token.warning', { tokenName })}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <Input
                        type="text"
                        value={clipboardInputValue}
                        className="rounded-tr-none rounded-br-none border-0"
                        readOnly
                      />

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={'raw'}
                              onClick={handleCopy}
                              className={
                                'px-3 rounded-md rounded-tl-none rounded-bl-none border-0 bg-background hover:bg-muted-foreground h-[40px]'
                              }
                            >
                              <CopyIcon size={20} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t('common.clipboard.tooltip')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          )}

          {apiTokens.length < 1 && (
            <CardContent className="flex flex-col items-center pt-6 m-auto w-1/2 text-center">
              <FileKeyIcon size={50} className="mb-5 stroke-primary" />

              <h2 className="text-4xl font-bold">{t('admin.api.token.first.generation.title')}</h2>

              <p className="py-5 text-muted-foreground">
                {t('admin.api.token.first.generation.description')}
              </p>
            </CardContent>
          )}

          <CardFooter className={`flex ${apiTokens.length > 0 ? 'justify-end' : 'justify-center'}`}>
            <Dialog open={newTokenDialogOpen} onOpenChange={setNewTokenDialogOpen}>
              <DialogTrigger asChild>
                <Button rounded="full">{t('admin.api.token.generation.button')}</Button>
              </DialogTrigger>

              <DialogContent className="p-10 !rounded-3xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl">
                    {t('admin.api.token.generation.dialog.title')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('admin.api.token.generation.dialog.description')}
                  </DialogDescription>
                </DialogHeader>

                <ApiTokenForm onSuccess={handleApiTokenGenerationSuccess} />
              </DialogContent>
            </Dialog>
          </CardFooter>
        </Card>
      )}

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="p-10 !rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {t('admin.api.token.delete.dialog.title')}
            </DialogTitle>
            <DialogDescription>{t('admin.api.token.delete.dialog.description')}</DialogDescription>
          </DialogHeader>

          <ApiTokenToDeleteForm
            tokenToDelete={tokenToDelete}
            onSuccess={handleApiTokenDeletionSuccess}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
